/**
 * Lo que cada pantalla explica cuando se pulsa el «?» de su cabecera.
 *
 * ## Qué va aquí y qué no
 *
 * Aquí van las reglas que **no se adivinan mirando la pantalla** y que han
 * costado una consulta: por qué el arqueo pide autorización, por qué un
 * descuento no se aplicó, por qué un cupón desapareció.
 *
 * No van las instrucciones de uso --dónde se pulsa para crear algo se ve
 * solo-- ni la explicación de un campo suelto, que corresponde a su propio
 * `tooltip` al lado. Y sobre todo: **si una regla necesita explicarse aquí,
 * primero hay que preguntarse si la pantalla puede decirlo en el momento**.
 * La ayuda es para lo genuinamente complejo, no para tapar lo que confunde.
 *
 * ## Por país
 *
 * Un punto con `paises` sólo se muestra en esos países. Sin `paises`, se
 * muestra siempre. Sirve para las reglas que de verdad cambian --los decimales
 * de Costa Rica, el comprobante fiscal-- sin llenar la ayuda de excepciones
 * que no aplican a quien la está leyendo.
 */

export const AYUDAS = {
  glosario: {
    titulo: "Qué es cada cosa",
    entradilla:
      "Cinco palabras que se confunden, y con razón: la misma sigla nombra tres cosas distintas según dónde se mire.",
    secciones: [
      {
        titulo: "La jerarquía, de fuera hacia dentro",
        puntos: [
          {
            texto:
              "Tienda: el local. Es lo que tiene dirección, serie de facturación y margen de cierre. Sus códigos son TIENDA_01, TIENDA_02… Una tienda contiene una o varias cajas.",
          },
          {
            texto:
              "Caja: el puesto de cobro dentro de una tienda. Es lo que se abre al empezar el día y se cierra al terminar, con su fondo y su arqueo. Sus códigos son TPV_01, TPV_02… y cada una pertenece a una tienda.",
          },
          {
            texto:
              "Cajero: la persona que atiende una caja. Es un usuario con su contraseña, y es quien queda registrado en cada venta del turno.",
          },
          {
            texto:
              "Terminal: el programa —esta aplicación—. No es un dato que se configure en ninguna pantalla: es lo que se está usando ahora mismo. Un terminal trabaja sobre una caja a la vez, la que tenga apertura activa.",
          },
        ],
      },
      {
        titulo: "Por qué «TPV» confunde",
        puntos: [
          {
            texto:
              "Porque nombra tres cosas distintas. En el menú y en los informes, TPV es la TIENDA. En el código de una caja —TPV_15— la sigla forma parte del nombre de la CAJA. Y en un documento de venta, el «Id TPV» no es ninguna de las dos: es el identificador de esa venta, el que evita que un reintento la duplique.",
          },
          {
            texto:
              "La regla que resuelve casi siempre: si el valor empieza por TIENDA_ es una tienda, y si empieza por TPV_ es una caja. El nombre del campo puede engañar; el del valor, no.",
          },
          {
            texto:
              "En la ficha de una caja, el campo que dice «TPV» contiene la tienda a la que pertenece, no la caja. Es el heredero de un nombre antiguo y por eso desconcierta: la caja TPV_01 tiene TPV = TIENDA_01.",
          },
        ],
      },
      {
        titulo: "Dónde aparece cada uno",
        puntos: [
          {
            texto:
              "Se elige la CAJA al abrir el turno; la tienda no se elige, viene dada por la caja. Por eso una caja mal asignada manda las ventas a la tienda equivocada sin que nadie lo teclee.",
          },
          {
            texto:
              "El margen que decide si el cierre pide autorización de un supervisor se configura en la TIENDA, no en la caja: todas las cajas de una tienda comparten el mismo margen.",
          },
          {
            texto:
              "La plantilla de cliente también es de la TIENDA. Un cliente dado de alta desde una caja hereda la plantilla de la tienda a la que esa caja pertenece.",
          },
        ],
      },
    ],
  },

  "como-va-el-turno": {
    titulo: "Cómo va el turno",
    entradilla: "Una foto del turno que está abierto, para consultarlo sin cerrar la caja.",
    secciones: [
      {
        titulo: "Qué se está viendo",
        puntos: [
          {
            texto:
              "Las ventas cobradas desde que se abrió la caja hasta ahora mismo. No se actualiza sola: para incorporar lo que se vendió después de entrar, hay que pulsar «Actualizar».",
          },
          {
            texto:
              "«Debería haber» es lo mismo que el cierre compara contra el conteo físico, calculado igual. Si al cerrar sale otro número, es porque entraron ventas entre una pantalla y la otra, no porque una de las dos esté mal.",
          },
          {
            texto:
              "En efectivo, «debería haber» incluye el fondo de apertura, porque el fondo se quedó en el cajón junto con las ventas. En las demás formas de pago, no.",
          },
        ],
      },
      {
        titulo: "Qué NO hace",
        puntos: [
          {
            texto:
              "No cierra la caja, no guarda nada y no envía nada a Business Central. Se puede abrir tantas veces como haga falta, y a mitad de una venta, sin consecuencias.",
          },
        ],
      },
      {
        titulo: "Cuándo sirve",
        puntos: [
          {
            texto:
              "Al entregar el turno, para comprobar lo que hay antes de firmar; y en una revisión a media jornada, cuando cerrar la caja sólo para mirar no es una opción.",
          },
          {
            texto:
              "Una forma de pago marcada «fuera del catálogo» se usó para cobrar y hoy ya no está en Formas de pago. Su dinero cuenta igual, pero conviene revisar por qué desapareció antes del cierre.",
          },
        ],
      },
    ],
  },

  "como-van-las-tiendas": {
    titulo: "Cómo van las tiendas",
    entradilla:
      "Lo cobrado en el día por cada tienda, para el supervisor: sus cajas, sus documentos y el cuadre por forma de pago.",
    secciones: [
      {
        titulo: "Qué se está viendo",
        puntos: [
          {
            texto:
              "Un resumen de todas las tiendas elegidas y, debajo, una sección por tienda: sus cajas con su cajero, turno, estado y fondo; una fila por documento con lo cobrado en cada forma de pago; y dos resúmenes por caja, uno por tipo de documento y otro por forma de pago.",
          },
          {
            texto:
              "Las columnas de formas de pago son las de esa tienda. Si se cobró con una que no es de la tienda, sale al final marcada «ajena», para que su dinero no se pierda del cuadre.",
          },
          {
            texto:
              "Las notas de crédito restan. Un documento marcado «Sin registrar» sigue en borrador en Business Central: conviene revisar por qué no se registró.",
          },
        ],
      },
      {
        titulo: "Cómo se consulta",
        puntos: [
          {
            texto:
              "Elige la fecha, las tiendas («Todas» las marca de una vez) y, si quieres acotar, sus cajas. No se consulta sola: pulsa «Consultar». Si cambias la selección después, la pantalla avisa de que lo que ves es de la consulta anterior.",
          },
          {
            texto:
              "Si eliges cajas, en cada tienda con alguna caja elegida sólo cuentan ésas; una tienda sin cajas elegidas se ve entera.",
          },
          {
            texto:
              "El resumen y cada tienda son grupos que se contraen, y dentro de cada tienda también cada tabla. «Expandir todo» y «Contraer todo» los abren o cierran de una vez.",
          },
        ],
      },
      {
        titulo: "De dónde salen los datos",
        puntos: [
          {
            texto:
              "Se leen de Business Central, no de este terminal. Se ve lo que ya llegó a BC: una venta que todavía no se envió desde su caja no aparece hasta que se envíe. Para ver lo que entró después, vuelve a pulsar «Consultar».",
          },
          {
            texto:
              "Cada documento cuenta en la tienda que dice Business Central, que es la de la caja que lo hizo, nunca por la serie de su número. Si llega sin tienda, cuenta en la tienda de su caja.",
          },
        ],
      },
      {
        titulo: "Para analizar",
        puntos: [
          {
            texto:
              "Cada columna se puede ordenar y filtrar desde su encabezado. En las de importe se filtra por con o sin importe, y por positivos o negativos. La tabla de documentos tiene además un buscador por número, caja, cajero o tipo.",
          },
          {
            texto:
              "Los totales de cada tabla suman lo que queda a la vista después de filtrar.",
          },
        ],
      },
      {
        titulo: "Qué NO hace",
        puntos: [
          {
            texto: "No cierra ninguna caja, no guarda nada y no envía nada a Business Central.",
          },
        ],
      },
    ],
  },
  "cierre-de-caja": {
    titulo: "Cierre de caja",
    entradilla:
      "El cierre compara lo que la caja debería tener contra lo que se contó, forma de pago por forma de pago.",
    secciones: [
      {
        titulo: "El arqueo",
        puntos: [
          {
            texto:
              "El conteo de efectivo incluye el fondo de apertura; las ventas del turno, no. Por eso lo esperado en efectivo es «ventas + fondo», y así las dos cifras son comparables.",
          },
          {
            texto:
              "Se compara cada forma de pago por separado, no el total. Si faltara efectivo y sobrara lo mismo en tarjeta, el total cuadraría y la diferencia quedaría tapada.",
          },
        ],
      },
      {
        titulo: "Cuándo se pide autorización",
        puntos: [
          {
            texto:
              "Cuando la diferencia de alguna forma de pago supera el margen configurado en la tienda. Basta con que lo supere una.",
          },
          {
            texto:
              "Si la tienda no tiene margen, se admite un céntimo de tolerancia: es ruido de redondeo, no un descuadre.",
          },
        ],
      },
      {
        titulo: "Antes de cerrar",
        puntos: [
          {
            texto:
              "Si el turno pasó sin una sola venta, se avisa. Da igual que la caja se abriera con fondo o sin él: lo que se pregunta es si hubo ventas, no si hay dinero en el cajón.",
          },
          {
            texto:
              "Un cierre ya sincronizado no se puede editar ni eliminar. Para corregirlo hay que hacerlo en Business Central.",
          },
        ],
      },
      {
        titulo: "Lo que viaja a Business Central",
        puntos: [
          {
            texto:
              "El número del cierre lleva la hora de APERTURA, no la de cierre. Es lo que distingue dos turnos de la misma caja el mismo día.",
          },
          {
            texto:
              "La hora del cierre viaja aparte. Se toma al guardar, no al enviar, para que un cierre hecho sin red y sincronizado al día siguiente conserve su hora real.",
          },
        ],
      },
    ],
  },

  "apertura-de-caja": {
    titulo: "Apertura de caja",
    entradilla:
      "La apertura declara con cuánto dinero empieza el turno. Todo lo que se cuente al cerrar se compara contra esa cifra.",
    secciones: [
      {
        titulo: "El fondo",
        puntos: [
          {
            texto:
              "El importe se arma contando billetes y monedas. Las denominaciones son las del país, y el fondo que se propone por defecto es el habitual de la tienda: se puede cambiar.",
          },
          {
            texto:
              "Ese fondo vuelve a aparecer en el cierre, sumado a lo esperado en efectivo. Si aquí se declara de más, al cerrar parecerá que falta dinero.",
          },
        ],
      },
      {
        titulo: "Una a la vez",
        puntos: [
          {
            texto:
              "No se puede abrir una caja mientras haya otra abierta. Hay que cerrar la anterior primero.",
          },
          {
            texto:
              "Business Central guarda una apertura por caja y día: si se abre y cierra varias veces en la misma jornada, allí queda la última. El terminal sí conserva las suyas por separado.",
          },
        ],
      },
    ],
  },

  maestros: {
    titulo: "Maestros",
    entradilla:
      "Los maestros --tiendas, cajas, usuarios, turnos, colegios y clientes-- se editan aquí y viajan a Business Central, que es quien manda.",
    secciones: [
      {
        titulo: "El código",
        puntos: [
          {
            texto:
              "Si se deja el código vacío, lo asigna Business Central al sincronizar. Si se escribe uno, se respeta tal cual: es lo que permite dar de alta con el código corporativo.",
          },
          {
            texto:
              "Una vez asignado, el código no se cambia: las cajas, los almacenes y los documentos lo usan como referencia.",
          },
        ],
      },
      {
        titulo: "Al eliminar",
        puntos: [
          {
            texto:
              "La baja se le pide a Business Central, no se hace sólo aquí. Si allí el registro está en uso --un cajero con aperturas, un turno con facturas registradas-- la baja se rechaza.",
          },
          {
            texto:
              "Cuando se rechaza, el registro vuelve al listado con el motivo que dio Business Central. No es que el borrado fallara a medias: es que las dos partes quedan iguales.",
          },
        ],
      },
      {
        titulo: "Pendiente de sincronizar",
        puntos: [
          {
            texto:
              "Un registro creado o modificado aquí queda pendiente hasta que viaja. Se puede seguir trabajando con él; el envío se reintenta solo o con el botón de la fila.",
          },
        ],
      },
      {
        titulo: "El cliente y la factura electrónica",
        puntos: [
          {
            paises: ["PA"],
            texto:
              "La factura electrónica de Panamá se arma con la ficha del cliente, no con la venta: de ahí salen el tipo de cliente, el RUC, la ubicación y el correo. Una ficha incompleta no impide cobrar, pero deja la venta esperando en el terminal.",
          },
          {
            paises: ["PA"],
            texto:
              "El tipo de cliente decide qué hace falta: un contribuyente o un gobierno necesitan además el dígito verificador del RUC y su razón social; un consumidor final, no. Un consumidor final tampoco puede llevar RUC de persona jurídica.",
          },
          {
            paises: ["PA"],
            texto:
              "La provincia, el distrito y el corregimiento se eligen de la lista de la Dirección General de Ingresos, y de ahí sale el código que viaja en el comprobante. Sin ese código, Business Central no registra la factura.",
          },
          {
            paises: ["PA"],
            texto:
              "El teléfono va con un solo número, como 263-7777 o 6123-4567. Dos números separados por una barra dejan la factura sin autorizar.",
          },
        ],
      },
    ],
  },

  productos: {
    titulo: "Productos y stock",
    entradilla:
      "El catálogo y las existencias son un espejo de Business Central: aquí se consultan, allí se mantienen.",
    secciones: [
      {
        titulo: "Qué stock se ve",
        puntos: [
          {
            texto:
              "El del almacén de la tienda en la que se está operando, no el de la compañía. Un producto puede existir y estar a cero aquí.",
          },
          {
            texto:
              "El stock se refresca al sincronizar y después de facturar. Entre medias es una foto, no un dato en vivo.",
          },
        ],
      },
      {
        titulo: "Precios",
        puntos: [
          {
            texto:
              "El precio sale del grupo de precios del cliente. Un mismo producto puede costar distinto según a quién se le venda.",
          },
        ],
      },
    ],
  },

  "traer-historicos": {
    titulo: "Traer históricos",
    entradilla:
      "Recupera de Business Central documentos que este terminal no tiene: los de otra jornada, otro turno u otro equipo.",
    secciones: [
      {
        titulo: "Qué trae",
        puntos: [
          {
            texto:
              "Los documentos del rango de fechas que se pida, para la caja en la que se está. Los que ya estaban aquí en ese rango se sustituyen por la versión que tiene Business Central, con sus líneas y sus pagos.",
          },
          {
            texto:
              "Nunca toca lo que este equipo todavía le debe a Business Central --lo que falta por enviar, lo que se está enviando y los borradores-- ni los documentos de otras cajas o fechas.",
          },
          {
            texto:
              "Lo que llega es lo que Business Central tiene registrado. Un documento que todavía esté pendiente de enviar desde otro terminal no aparecerá.",
          },
          {
            texto:
              "Al terminar pregunta el timbrado de lo que trajo, empezando por lo más reciente y como mucho 120 documentos cada vez. El resto se completa al actualizar correlativos o al reimprimir.",
            paises: ["CR"],
          },
        ],
      },
    ],
  },

  "impresion-de-tickets": {
    titulo: "Impresión de tickets",
    entradilla: "Cómo sale el papel: qué formato, con qué márgenes y con cuántos decimales.",
    secciones: [
      {
        titulo: "Formato",
        puntos: [
          {
            texto:
              "«Predeterminado» es el ticket de siempre. «Personalizado» es el formato propio del país. «Ambos» dibuja los dos con un selector arriba, para compararlos contra el papel antes de decidir.",
          },
          {
            texto: "En un país sin formato propio, las tres opciones imprimen el predeterminado.",
          },
        ],
      },
      {
        titulo: "Decimales",
        puntos: [
          {
            texto:
              "Son los decimales con los que se ESCRIBE el importe en el papel, no con los que se calcula. Cambiarlos no altera ningún total.",
          },
          {
            texto:
              "Aquí se calcula con cinco decimales y conviene imprimir con dos, que es lo que dice el comprobante.",
            paises: ["CR"],
          },
        ],
      },
      {
        titulo: "Envío a la impresora",
        puntos: [
          {
            texto:
              "«Imagen» convierte el ticket en una foto antes de imprimir, pensado para térmicas. «Texto» lo envía como texto real: pruébelo si no imprime bien en una impresora de agujas.",
          },
        ],
      },
    ],
  },

  facturacion: {
    titulo: "Facturación",
    entradilla:
      "Cada venta se guarda primero en el terminal y después viaja a Business Central. Las dos cosas pueden ir a distinto ritmo.",
    secciones: [
      {
        titulo: "Descuentos: no se acumulan",
        puntos: [
          {
            texto:
              "El descuento por forma de pago se anula si alguna línea lleva descuento. No se suman: o uno, o el otro.",
          },
          {
            texto:
              "Con dos o más formas de pago no hay descuento por forma de pago, porque no habría a cuál atribuirlo.",
          },
        ],
      },
      {
        titulo: "Reintentos y duplicados",
        puntos: [
          {
            texto:
              "Cada documento lleva un identificador propio con la caja, la fecha, la hora y un contador. Si un envío falla y se reintenta, Business Central reconoce que es la misma venta y no la duplica.",
          },
          {
            texto:
              "Si Business Central devuelve un número de documento que no se puede usar, el envío se detiene con el motivo a la vista en vez de seguir adelante.",
          },
          {
            texto:
              "El terminal crea el documento; contabilizarlo es cosa de Business Central. Hasta entonces la factura aparece con su número preasignado y sin número definitivo.",
          },
        ],
      },
      {
        titulo: "Importes",
        puntos: [
          {
            texto:
              "Un importe lleva tres cifras de decimales distintas y conviene no confundirlas: con cuántos se CALCULA, con cuántos se ESCRIBE en pantalla y con cuántos se IMPRIME. Pueden no coincidir, y que coincidan en un país no las hace la misma cosa.",
          },
          {
            texto:
              "Aquí se calcula con cinco decimales, la pantalla los escribe con cinco y el papel imprime con dos.",
            paises: ["CR"],
          },
          {
            texto:
              "Lo que la pantalla muestra se puede teclear tal cual en cualquier campo de importe: los separadores de miles y de decimales son los del país.",
          },
          {
            texto:
              "Las cantidades se escriben enteras cuando lo son, y los porcentajes siempre con dos decimales.",
          },
        ],
      },
      {
        titulo: "Cobro y cambio",
        puntos: [
          {
            texto:
              "Sólo con EFECTIVO se puede recibir de más y devolver la diferencia. El formulario ofrece el cálculo de cambio y acepta un importe mayor que la venta.",
          },
          {
            texto:
              "Con tarjeta, transferencia o cualquier otra forma de pago no se admite cobrar de más: no hay cambio que devolver por esa vía. El importe se topa en lo que queda pendiente.",
          },
          {
            texto:
              "Lo que decide es la CLASIFICACIÓN de la forma de pago, no su nombre, y se ve en la cajita de color junto a cada una. Una forma de pago sin clasificar no admite cobrar de más; si debiera hacerlo, se corrige en su ficha del maestro.",
          },
        ],
      },
      {
        titulo: "Notas de crédito",
        puntos: [
          {
            texto:
              "Una nota de crédito parte siempre de un documento existente: se elige y desde ahí se toman sus líneas.",
          },
          {
            texto:
              "Puede corregir una factura o un tiquete, que ante Hacienda son documentos distintos. El listado de abonos lo dice junto al tipo; cuando no lo sabe —los abonos traídos de antes— no lo muestra en vez de suponerlo.",
            paises: ["CR"],
          },
          {
            texto:
              "Las cantidades que ya se abonaron no se pueden volver a abonar; el formulario sólo ofrece lo que queda.",
          },
        ],
      },
      {
        titulo: "El estado de cada documento",
        puntos: [
          {
            texto:
              "La columna Estado dice si el documento ya llegó a Business Central: Borrador, Pendiente, Sincronizada, Error o Eliminada. Si en su filtro se marcan varias opciones, se ven las que cumplan cualquiera de ellas.",
          },
          {
            texto:
              "La misma columna enseña el timbrado, y su filtro también lo ofrece: Timbrado, Timbre pendiente, Sin timbrar y Rechazado por Hacienda. Rechazado tiene arreglo --se corrige el dato y se vuelve a timbrar desde Business Central--; Sin timbrar no se arregla esperando.",
            paises: ["CR"],
          },
          {
            texto:
              "«Sin consultar el timbre» filtra las contabilizadas de las que este equipo todavía no preguntó. No quiere decir que les falte el timbre: Actualizar correlativos lo pregunta.",
            paises: ["CR"],
          },
        ],
      },
    ],
  },

  cupones: {
    titulo: "Cupones",
    entradilla:
      "La lista del terminal es un espejo de los cupones activos en Business Central, no un archivo propio.",
    secciones: [
      {
        titulo: "Qué hace sincronizar",
        puntos: [
          {
            texto:
              "Reemplaza la lista entera. Lo que Business Central ya no devuelve como activo, desaparece del terminal.",
          },
          {
            texto:
              "Un cupón deja de venir cuando se le quita la vigencia o cuando se le apaga el interruptor de cupón para dejarlo como campaña. En los dos casos desaparece de aquí al sincronizar.",
          },
          {
            texto:
              "«No hay cupones activos» es una respuesta válida, no un fallo de conexión. Si de verdad no se pudo conectar, el mensaje lo dice y la lista local se conserva intacta.",
          },
        ],
      },
      {
        titulo: "Al aplicarlos",
        puntos: [
          {
            texto:
              "El descuento del cupón entra como descuento de línea, así que anula el descuento por forma de pago de esa venta.",
          },
          {
            texto:
              "Un cupón fuera de vigencia no se puede aplicar aunque siga viéndose en pantalla hasta la próxima sincronización.",
          },
        ],
      },
    ],
  },
  campanas: {
    titulo: "Campañas",
    entradilla:
      "Una campaña es un descuento que se aplica solo, sin que el cajero escriba ningún código. La lista es un espejo de Business Central, igual que la de cupones.",
    secciones: [
      {
        titulo: "Cuándo está activa",
        puntos: [
          {
            texto:
              "Lo deciden sus fechas. Una campaña sin fecha de inicio ni de fin se considera activa siempre.",
          },
          {
            texto:
              "La columna Vigencia dice lo que se calcula hoy; el interruptor de Business Central es otra cosa y puede desactivar una campaña que por fechas seguiría vigente.",
          },
        ],
      },
      {
        titulo: "Al aplicarla",
        puntos: [
          {
            texto:
              "Su descuento entra como descuento de línea, así que anula el descuento por forma de pago de esa venta. No se suman: o uno, o el otro.",
          },
          {
            texto:
              "Cambiar el colegio o el cliente de una factura vuelve a calcular las líneas, y con ellas la campaña que corresponde.",
          },
        ],
      },
    ],
  },

  "formas-de-pago": {
    titulo: "Formas de pago",
    entradilla:
      "Cada forma de pago tiene un tipo, y el tipo decide más cosas de las que parece: cómo se cobra, cómo se cuadra el cierre y de qué color se ve.",
    secciones: [
      {
        titulo: "El tipo manda",
        puntos: [
          {
            texto:
              "Solo el efectivo admite cobrar de más y devolver cambio. Con tarjeta, transferencia o cheque no se puede registrar un importe mayor al pendiente de la venta.",
          },
          {
            texto:
              "Una forma de pago sin clasificar tampoco lo admite, y se muestra como «Sin clasificar» en vez de darse por efectivo, que sería justo lo contrario de lo que hace la regla de cobro.",
          },
          {
            texto:
              "El color de cada tipo es el mismo en el cobro, en este maestro y en el detalle del cierre de caja.",
          },
        ],
      },
      {
        titulo: "El descuento",
        puntos: [
          {
            texto:
              "El descuento de la forma de pago se anula si alguna línea de la venta lleva descuento propio, venga de un cupón, de una campaña o del cliente.",
          },
          {
            texto:
              "Con dos o más formas de pago en la misma venta no se aplica, porque no habría a cuál atribuirlo.",
          },
        ],
      },
    ],
  },

  "precios-de-venta": {
    titulo: "Precios de venta",
    entradilla:
      "Un producto puede tener varios precios a la vez. El que se aplica no es el más barato ni el último: es el primero de una lista de prioridades.",
    secciones: [
      {
        titulo: "Qué precio gana",
        puntos: [
          {
            texto:
              "Se busca en este orden y gana el primero que exista: precio del cliente, precio de su grupo de precios, precio para todos los clientes, y por último el precio del producto.",
          },
          {
            texto:
              "Por eso cambiar el cliente o el colegio de una factura ya empezada vuelve a calcular las líneas: el precio depende de a quién se le vende.",
          },
        ],
      },
      {
        titulo: "Las fechas",
        puntos: [
          {
            texto:
              "Un precio con fecha de inicio o de fin solo se aplica dentro de ese rango. Fuera de él se pasa al siguiente de la lista de prioridades, no se deja el producto sin precio.",
          },
        ],
      },
    ],
  },

  informes: {
    titulo: "Informes",
    entradilla:
      "Los informes se pueden calcular con lo que hay en el terminal o pidiéndoselo a Business Central. No siempre dan lo mismo, y eso es información.",
    secciones: [
      {
        titulo: "Las dos fuentes",
        puntos: [
          {
            texto:
              "El interruptor de la cabecera elige la fuente. El terminal responde al instante y solo sabe de lo que tiene; Business Central sabe de todo pero solo de lo que ya llegó.",
          },
          {
            texto:
              "Una diferencia entre las dos suele significar que hay documentos sin sincronizar. El aviso de la cabecera dice cuántos.",
          },
        ],
      },
      {
        titulo: "Qué importe se informa",
        puntos: [
          {
            texto:
              "La venta neta, es decir con el descuento por forma de pago ya aplicado. Es el importe que se cobró y el que lleva el comprobante, no el de la suma de los productos.",
          },
          {
            texto:
              "El «cobrado» del arqueo se compara contra el dinero registrado, no contra el total de los documentos: cuando hay descuento por forma de pago, los dos números no son el mismo.",
          },
        ],
      },
    ],
  },

  configuracion: {
    titulo: "Configuración",
    entradilla:
      "Casi todo lo de aquí es de este equipo. Dos pestañas no: afectan a todos los terminales del entorno.",
    secciones: [
      {
        titulo: "Lo que afecta a todos",
        puntos: [
          {
            texto:
              "Las acciones de Mantenimiento se ejecutan en Business Central, así que alcanzan a todos los equipos del entorno. Por eso piden confirmación y muestran el resultado.",
          },
          {
            texto:
              "Parámetros regionales es solo de consulta: enseña lo que el país tiene configurado, no se cambia desde aquí.",
          },
        ],
      },
      {
        titulo: "Lo que es de este equipo",
        puntos: [
          {
            texto:
              "El tema, la impresión de tickets y las preferencias de sincronización se guardan en este terminal, no en el usuario. La misma persona puede tener cada caja distinta.",
          },
          {
            texto:
              "Borrar todos los maestros no toca Business Central: vacía este equipo y la siguiente sincronización lo vuelve a llenar. Conserva al usuario con la sesión abierta.",
          },
          {
            texto:
              "El botón Eliminar de cada renglón vacía esa tabla sólo en este equipo. En Facturas, Aperturas de caja y Cierres de caja avisa antes cuántos documentos hay sin enviar a Business Central: si continúas, se pierden, porque nunca llegaron allá.",
          },
          {
            texto:
              "Al arrancar, la caja le pide al navegador que no borre sus datos aunque falte espacio en el disco: ahí esperan las ventas que todavía no subieron. Si el navegador no lo concede, la caja funciona igual, pero conviene no dejar ventas sin sincronizar.",
          },
        ],
      },
    ],
  },

  // La ayuda de Inicio sí explica etiqueta por etiqueta: lo pidió Solme el
  // 17/09/2026 para que el cajero entienda cada cifra del tablero. Una prueba
  // comprueba que todas las etiquetas que pinta el tablero aparezcan aquí.
  inicio: {
    titulo: "Inicio",
    entradilla:
      "Un resumen de lo que pasó en el período que elijas arriba: Hoy, Semana, Mes o un rango de fechas. Todo sale de este equipo, así que también funciona sin conexión.",
    secciones: [
      {
        titulo: "Las cuatro cifras de arriba",
        puntos: [
          {
            texto:
              "Ventas netas: lo facturado menos lo devuelto en notas de crédito, con el descuento de la forma de pago ya aplicado. Es la cifra que más se parece a lo que debería cuadrar al cerrar.",
          },
          {
            texto:
              "Ticket promedio: lo facturado dividido entre el número de facturas. Las notas de crédito no entran en esta cuenta.",
          },
          {
            texto:
              "Facturas vs. notas de crédito: cuántas hay de cada una en el período, con lo vendido y lo devuelto en dinero.",
          },
          {
            texto:
              "Errores de sincronización: documentos del período que no se pudieron enviar a Business Central. Cuáles son y por qué está en «Necesitan atención».",
          },
        ],
      },
      {
        titulo: "Ventas por forma de pago",
        puntos: [
          {
            texto:
              "Cuánto entró por cada forma de pago: lo cobrado en facturas menos lo devuelto en notas de crédito, igual que lo cuenta el cierre de caja.",
          },
          {
            texto:
              "Total neto es la suma de todas las formas de pago. Si no coincide con Ventas netas, a su izquierda aparece «Difiere de Ventas netas» con la diferencia y los documentos que la explican: por ejemplo, una nota de crédito a la que nadie le registró cómo se devolvió el dinero, o una factura cobrada de menos o de más.",
          },
          {
            texto:
              "Una forma de pago con importe negativo devolvió más de lo que cobró en el período. Se lista con su importe, pero no ocupa espacio en el círculo.",
          },
        ],
      },
      {
        titulo: "Cajas, productos y clientes",
        puntos: [
          {
            texto:
              "Ventas por caja y turno: lo facturado en cada caja y turno del período. La primera, marcada como mejor caja, es la que más vendió.",
          },
          {
            texto:
              "Productos más vendidos: los que más unidades vendieron en el período, con su importe.",
          },
          {
            texto:
              "Clientes: cuántos hay registrados en este equipo y cuántos se dieron de alta en el período. Mejores clientes: los cinco que más compraron.",
          },
          {
            texto:
              "Cupones y campañas: cuántas ventas usaron cada uno y cuánto descuento se otorgó con ellos.",
          },
          {
            texto:
              "Apertura y cierre: la última apertura y el último cierre de esta caja, sin importar el período que elegiste arriba.",
          },
        ],
      },
      {
        titulo: "Estado de los documentos",
        puntos: [
          {
            texto:
              "Es el camino de las facturas y notas de crédito del período. El porcentaje de cada paso es sobre el paso anterior: así se ve dónde se quedan.",
          },
          {
            texto:
              "Emitidas: todas las del período, sin contar borradores. En Business Central: las que ya llegaron; las demás siguen en este equipo esperando enviarse.",
          },
          {
            texto:
              "Contabilizadas: las que Business Central ya registró. Las que llegaron y todavía no están contabilizadas esperan a que se registren allá.",
          },
          {
            texto:
              "Timbradas: las contabilizadas que Hacienda ya selló, con su clave de 50 dígitos.",
            paises: ["CR"],
          },
          {
            texto:
              "«Sin consultar su timbre» quiere decir que este equipo todavía no preguntó, no que falte el timbre. Actualizar correlativos o traer históricos lo pregunta.",
            paises: ["CR"],
          },
        ],
      },
      {
        titulo: "Necesitan atención",
        puntos: [
          {
            texto:
              "Rechazadas por Hacienda: tienen arreglo. Se corrige el dato que dice el mensaje --un teléfono con guiones, por ejemplo-- y se vuelve a timbrar desde Business Central.",
            paises: ["CR"],
          },
          {
            texto:
              "No se van a timbrar: Business Central dice por qué, y no se arregla esperando. Suele ser una nota de crédito que no indica qué factura corrige.",
            paises: ["CR"],
          },
          {
            texto:
              "Frenadas, sin reintento: la caja ya no las vuelve a mandar, porque mandarlas igual daría el mismo error. Hay que corregir lo que dice el motivo y sincronizar.",
          },
          {
            texto:
              "Esperando reintento: fallaron por algo pasajero, como la conexión, y la caja las vuelve a intentar sola. Sólo preocupan si se quedan ahí mucho tiempo.",
          },
          {
            texto:
              "De cada tipo se muestran las tres más recientes, con su número y el motivo, para que sepas cuál buscar en el listado de facturas.",
          },
        ],
      },
      {
        titulo: "Sin enviar a Business Central",
        puntos: [
          {
            texto:
              "Cuántas ventas de este equipo siguen sin subir, de cualquier fecha, aunque el período elegido arriba sea «Hoy».",
          },
          {
            texto:
              "La más vieja dice hace cuánto está esperando. Una venta sin subir sólo existe en este equipo: no borres sus datos y sincroniza en cuanto haya conexión.",
          },
        ],
      },
    ],
  },
};

/**
 * Las secciones de una pantalla, ya filtradas por el país en el que se opera.
 *
 * Una sección que se queda sin puntos no se devuelve: un título solo, sin nada
 * debajo, es peor que no mostrar la sección.
 *
 * @param {string} clave        Cuál de las ayudas.
 * @param {string} [codigoPais] Código del país activo.
 */
export const ayudaDePantalla = (clave, codigoPais) => {
  // Admite varias claves porque hay contenido que no es de una pantalla sino
  // transversal --el glosario de tienda, caja y terminal-- y hace falta justo
  // donde se configuran esas cosas. La alternativa era elegir entre la ayuda de
  // la pantalla y el glosario, y las dos hacen falta en Tiendas y en Cajas.
  //
  // El título y la entradilla los pone la PRIMERA clave: es la ayuda de la
  // pantalla, y lo transversal va detrás como una sección más.
  const claves = Array.isArray(clave) ? clave : [clave];
  const ayudas = claves.map((c) => AYUDAS[c]).filter(Boolean);
  if (!ayudas.length) return null;

  const delPais = (secciones) =>
    secciones
      .map((seccion) => ({
        ...seccion,
        puntos: seccion.puntos.filter((p) => !p.paises || p.paises.includes(codigoPais)),
      }))
      .filter((seccion) => seccion.puntos.length > 0);

  return { ...ayudas[0], secciones: ayudas.flatMap((a) => delPais(a.secciones)) };
};
